import PropTypes from 'prop-types';
import React, { useEffect, useState, useMemo } from 'react';
import { debounce, isNil } from 'lodash';
import { formatToPrecision, parseFloatValue } from './util';
import Input from './Input';
import './Input.scss';
import { useComponentVisible } from '../../../hooks/useComponentVisible';

const DEFAULT_DECIMAL_PRECISION = 6;

const getPercentFromValue = (apr) =>
  formatToPrecision(parseFloatValue(apr) * 100, DEFAULT_DECIMAL_PRECISION);

const getValueFromPercent = (percent) => parseFloatValue(percent) * 0.01;

function PercentageInput(props) {
  const { ref, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(true);
  const { value } = props;
  const getValue =
    isNil(value) || (!props.placeholder && value === 0)
      ? ''
      : getPercentFromValue(value);

  const [displayValue, setDisplayValue] = useState(getValue);
  const [hasFocus, setHasFocus] = useState(false);

  const doDebounce = useMemo(
    () =>
      debounce((percent) => {
        if (percent === '') {
          props.onChange(undefined);
        } else {
          props.onChange(getValueFromPercent(percent));
        }
      }, 500),
    [props.onChange],
  );

  const debounceHandler = (percent) => {
    setDisplayValue(percent);
    doDebounce(percent);
  };

  useEffect(() => {
    if (getValue !== displayValue && !hasFocus) {
      setDisplayValue(getValue);
    }
  }, [value]);

  useEffect(() => {
    // if clicked outside, set the current value to display value.
    if (!isComponentVisible) {
      if (getValue !== displayValue) {
        setDisplayValue(getValue);
      }
    }
  }, [isComponentVisible]);

  return (
    <div className="finance-input percentage-input-container" ref={ref}>
      <Input
        type="number"
        className={`percentage-input ${props.className ? props.className : ''}`}
        disabled={props.disabled}
        required={props.required}
        onChange={debounceHandler}
        value={displayValue}
        title={props.title}
        placeholder={props.placeholder ?? 0}
        onFocus={() => {
          setHasFocus(true);
          setIsComponentVisible(true);
        }}
        onBlur={() => setHasFocus(false)}
        step={props.step}
        onKeyDown={props.onKeyDown}
      />
      {props.displayPercentSign && '%'}
    </div>
  );
}

PercentageInput.defaultProps = {
  displayPercentSign: true,
};

PercentageInput.propTypes = {
  value: PropTypes.number,
  title: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  displayPercentSign: PropTypes.bool,
  step: PropTypes.string,
  onKeyDown: PropTypes.func,
};

export default PercentageInput;
